import { decryptField, encryptField, looksLikeCiphertext } from "@paisa/crypto"

// Key derivation, DEK wrapping, field encryption and recovery live in
// @paisa/crypto (shared with the MCP server); re-exported so callers keep
// importing from "@/lib/crypto".
export * from "@paisa/crypto"

// ─── Fields that get encrypted in private mode ────────────────────────────────
const ENCRYPTED_TXN_FIELDS = ["description", "referenceNumber", "notes"] as const

// ─── Standard mode no-ops ────────────────────────────────────────────────────
export async function encryptFieldMaybeNoop(
  plaintext: string | null | undefined,
  key: CryptoKey | null,
  mode: "standard" | "private",
): Promise<string | null | undefined> {
  if (!plaintext) return plaintext
  if (mode === "standard" || !key) return plaintext
  return encryptField(plaintext, key)
}

export async function decryptFieldMaybeNoop(
  value: string | null | undefined,
  key: CryptoKey | null,
  mode: "standard" | "private",
): Promise<string | null | undefined> {
  if (!value) return value
  // No key here (standard mode, or a household member who hasn't unlocked) but
  // the value is ciphertext written by someone else: never show base64 noise.
  if (mode === "standard" || !key) return looksLikeCiphertext(value) ? LOCKED : value
  try {
    return await decryptField(value, key)
  } catch {
    if (looksLikeCiphertext(value)) return UNDECRYPTABLE
    return value // Return as-is if decryption fails (e.g. unencrypted legacy data)
  }
}

// ─── Transaction row decryption (private mode display) ───────────────────────
// In private mode these fields are stored as ciphertext. The browser holds the
// DEK after PIN unlock and decrypts them client-side for display. No-op in
// standard mode or before unlock (key null). Uses ENCRYPTED_TXN_FIELDS above.

export async function decryptTransactionRows<
  T extends {
    description?: unknown
    referenceNumber?: unknown
    notes?: unknown
    unencrypted?: boolean
  },
>(
  rows: T[] | undefined,
  key: CryptoKey | null,
  _mode?: "standard" | "private",
): Promise<T[] | undefined> {
  // A DEK only ever exists in private mode (standard mode derives no key), so
  // keying on `key` presence alone is correct AND robust even if the store's
  // `mode` flag wasn't set on unlock. decryptField fails closed on plaintext.
  if (!rows) return rows
  if (!key) {
    // Without a key, rows another household member encrypted would render as
    // base64 noise (UAT J9-01). Show a label instead; plaintext is untouched.
    for (const row of rows) {
      const r = row as Record<string, unknown>
      for (const field of ENCRYPTED_TXN_FIELDS) {
        const val = r[field]
        if (typeof val === "string" && looksLikeCiphertext(val)) r[field] = LOCKED
      }
    }
    return rows
  }
  for (const row of rows) {
    const r = row as Record<string, unknown>
    for (const field of ENCRYPTED_TXN_FIELDS) {
      const val = r[field]
      if (typeof val !== "string" || !val) continue
      try {
        r[field] = await decryptField(val, key)
      } catch {
        // Fail closed, without hiding real data:
        //  - ciphertext that won't decrypt (tampered, or written under another key)
        //    shows a label instead of base64 noise;
        //  - plaintext is kept but the row is flagged, so text the server planted
        //    can't pass for the user's own encrypted data.
        if (looksLikeCiphertext(val)) r[field] = UNDECRYPTABLE
        else r.unencrypted = true
      }
    }
  }
  return rows
}

/** Shown in place of a private-mode field whose ciphertext won't decrypt. */
export const UNDECRYPTABLE = "🔒 Can't decrypt"

/** Shown in place of a field another household member encrypted, when this person has no key. */
export const LOCKED = "🔒 Private"

// ─── Transaction field encryption (private mode writes) ─────────────────────
// The write-side counterpart of decryptTransactionRows: encrypts every
// ENCRYPTED_TXN_FIELDS value with encryptFieldMaybeNoop (the helper the Vault
// import already uses), so a manual add / quick add stores the same
// ciphertext format. Returns a copy; empty/absent fields stay as they are.
export async function encryptTransactionFields<
  T extends { description?: string; referenceNumber?: string; notes?: string },
>(body: T, key: CryptoKey | null, mode: "standard" | "private"): Promise<T> {
  const out = { ...body } as Record<string, unknown>
  for (const field of ENCRYPTED_TXN_FIELDS) {
    const val = out[field]
    if (typeof val === "string" && val) out[field] = await encryptFieldMaybeNoop(val, key, mode)
  }
  return out as T
}

/**
 * Every client write of transaction text (create, edit) goes through this:
 * private mode without the key fails closed, otherwise text fields are
 * encrypted (a no-op in standard mode). A DEK only exists in private mode, so
 * key presence decides, as in decryptTransactionRows.
 */
export async function prepareTransactionWrite<
  T extends { description?: string; referenceNumber?: string; notes?: string },
>(body: T, key: CryptoKey | null, mode: "standard" | "private"): Promise<T> {
  if (mode === "private" && !key) throw new Error("Unlock Paisa to save this transaction.")
  return encryptTransactionFields(body, key, key ? "private" : mode)
}
